import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import i18n from 'i18next';
import { DEFAULT_ADDITIONAL_SALARY_TYPES } from 'LixaniAPI/enums';
import { round, uniq } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { generateBalancoSalaryReport } from 'scenes/WorkDemo/store/thunks/reports/generateBalancoSalaryReport';
import { generateReportForClientFromSelectedRecords } from 'scenes/WorkDemo/store/thunks/reports/generateReportForClientFromSelectedRecords.thunk';
import { generateReportFromSelectedRecords } from 'scenes/WorkDemo/store/thunks/reports/generateReportFromSelectedRecords.thunk';
import { generateReportFromWorkCalendarData } from 'scenes/WorkDemo/store/thunks/reports/generateReportFromWorkCalendarData.thunk';
import { generateReportPreviewTableFromSelectedRecords } from 'scenes/WorkDemo/store/thunks/reports/generateReportPreviewTableFromRecords.thunk';
import { generateSalaryRowDataFromRecords } from 'scenes/WorkDemo/store/thunks/reports/generateSalaryRowDataFromRecords';
import { generateSalaryRows } from 'scenes/WorkDemo/store/thunks/reports/generateSalaryRows';
import { generateSubcontractorReportFromSelectedRecords } from 'scenes/WorkDemo/store/thunks/reports/generateSubcontractorReportFromSelectedRecords.thunk';
import { arrayToMap } from 'utils/arrayToMap';
import { returnDateAndIsoWeekDayFromReportDayValue } from 'utils/returnDateAndIsoWeekDayFromReportDayValue.util';
import {
  addAdditionalUserSalaryRow,
  addManualUserRowTotals,
  addSalaryRowsRecipientEmail,
  closeAdditionalUserSalaryRowDialog,
  closeConfirmReportDraftDeleteDialog,
  closeReportDraftsDialog,
  closeReportPreviewDialog,
  closeReportSendDialog,
  closeRowCommentDialog,
  initAddEmployeeForm,
  openAdditionalUserSalaryRowDialog,
  openConfirmReportDraftDeleteDialog,
  openNextReport,
  openPreviousReport,
  openReportDraftsDialog,
  openReportSendDialog,
  openReportsHistory,
  openRowCommentDialog,
  removeReportPreviewUserAdditionalSalaryRow,
  removeReportPreviewUserRow,
  removeSalaryRowsRecipientEmail,
  resetLoadingReportState,
  resetPreReportDialogSettings,
  setDayWorkHoursOfEmployeeToAdd,
  setEmployeeNameToAdd,
  setInitialReportEditValuesOfDraft,
  setIsConfirmJydacomSalaryDialogOpen,
  setPaidCoffeeBreakMinutes,
  setReportGeneratedFromMode,
  setRowComment,
  setSelectedReportDraftId,
  toggleIncludeOnlyApprovedRecords,
  toggleIsAutomaticMealBreakReductionsAppliedToRecordBasedReductions,
  toggleIsReportProjectRowsEnabled,
  toggleRecordBasedReductions,
} from './actions/reports.actions';
import { addReportPreviewUserRow } from './thunks/addReportPreviewUserRow.thunk';
import { deleteReportDraft } from './thunks/deleteReportDraft.thunk';
import { generateProjectEmployeeReport } from './thunks/generateProjectEmployeeReport.thunk';
import { generateProjectListReport } from './thunks/generateProjectListReport.thunk';
import { generateProjectOrientatedEmployeesReport } from './thunks/generateProjectOrientatedEmployeesReport.thunk';
import { loadProjectSavedReports } from './thunks/loadProjectSavedReports';
import { sendJydacomSalaryData } from './thunks/sendJydacomSalaryData.thunk';
import { sendSalaryData } from './thunks/sendSalaryData.thunk';

const savedReportsAdapter = createEntityAdapter({
  selectId: (savedReport) => `${savedReport.PK}`,
});

export const reportsSlice = createSlice({
  name: 'reports',
  initialState: {
    generatedReport: {},
    isRecordBasedReductionsActive: false,
    isAutomaticMealBreakReductionsAppliedToRecordBasedReductions: false,
    isReportProjectHourRowsEnabled: false,
    isIncludeOnlyApprovedRecordsEnabled: false,
    reportProcessedData: {},
    reportBasicData: {},
    reportGeneratedFromMode: 'ALL',
    reportRecipientEmails: [],
    tableDataByWeekPeriod: [],
    weekIndexedEmployeeNamesToAdd: {},
    weekIndexedDayWorkHoursOfEmployeeToAdd: {},
    reportUserRowTotals: {},
    rowComments: {},
    selectedRowCommentData: {},
    additionalUserSalaryRows: {},
    seperateAdditionalUserSalaryRows: {},
    selectedReportDraftId: '',
    processedSalaryRows: [],
    salaryErrorMessage: '',
    paidCoffeeBreakMinutes: 15,
    // loading states
    loading: false,
    isSendingSalaryData: false,
    isDeletingReportDraft: false,
    // dialogs
    isReportSendDialogOpen: false,
    isRowCommentDialogOpen: false,
    isAdditionalSalaryRowDialogOpen: false,
    isReportDraftsDialogOpen: false,
    isConfirmReportDraftDeleteDialogOpen: false,
    additionalUserSalaryRowDialogData: {},
    isConfirmJydacomSalaryDialogOpen: false,
    // saved reports
    savedReports: savedReportsAdapter.getInitialState(),
    isReportsHistoryOpen: false,
    currentOpenHistoryReportIndex: 0,
  },
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(generateReportFromSelectedRecords.pending, (state, action) => {
        state.generatedReport = {};
        state.loading = true;
      })
      .addCase(generateReportFromSelectedRecords.fulfilled, (state, action) => {
        state.generatedReport = action.payload.documentData;
        state.reportProcessedData = action.payload.processedData;
        state.loading = false;
      })
      .addCase(generateReportFromWorkCalendarData.pending, (state, action) => {
        state.generatedReport = {};
        state.loading = true;
      })
      .addCase(
        generateReportFromWorkCalendarData.fulfilled,
        (state, action) => {
          state.generatedReport = action.payload.documentData;
          state.loading = false;
        }
      )
      .addCase(
        generateReportPreviewTableFromSelectedRecords.pending,
        (state, action) => {
          state.loading = true;
        }
      )
      .addCase(
        generateSubcontractorReportFromSelectedRecords.pending,
        (state, action) => {
          state.generatedReport = {};
          state.loading = true;
        }
      )
      .addCase(
        generateSubcontractorReportFromSelectedRecords.fulfilled,
        (state, action) => {
          state.generatedReport = action.payload.documentData;
          state.reportProcessedData = action.payload.processedData;
          state.loading = false;
        }
      )
      .addCase(
        generateReportForClientFromSelectedRecords.pending,
        (state, action) => {
          state.generatedReport = {};
          state.loading = true;
        }
      )
      .addCase(
        generateReportForClientFromSelectedRecords.fulfilled,
        (state, action) => {
          state.generatedReport = action.payload.documentData;
          state.reportProcessedData = action.payload.processedData;
          state.loading = false;
        }
      )
      .addCase(
        generateReportPreviewTableFromSelectedRecords.fulfilled,
        (state, action) => {
          const processedData = action.payload.processedData;

          const isRecordBasedReductionsActive =
            state.isRecordBasedReductionsActive;

          state.reportBasicData = {
            fromDate: processedData.fromDate,
            toDate: processedData.toDate,
            reportType: processedData.reportType,
            isRecordBasedReductionsActive,
          };

          const tableRowsByWeekIndex = processedData.dataRows;

          const weekTablesData = processedData.headerRows.map(
            (headerRow, index) => ({
              headerRow: headerRow,
              dataRows: tableRowsByWeekIndex[index],
            })
          );

          state.tableDataByWeekPeriod = weekTablesData;

          let totalsToSet = {};

          const userTotalWorkAmountsByUserId =
            processedData.userTotalWorkAmountsByUserId;

          Object.values(userTotalWorkAmountsByUserId).forEach(
            (userTotalAmount) => {
              const userId = userTotalAmount.userId;
              const userResourceData = processedData.resources[userId];

              const {
                baseSalaryType,
                unitPrice,
                projectTotals,
                eachDayTotalHours,
                assignmentTotals,
              } = userTotalAmount;

              const isMonthlyBaseSalary = baseSalaryType === 'MONTHLY';

              const salaryType = isMonthlyBaseSalary
                ? 'WORK_SALARY_MONTHLY'
                : 'WORK_SALARY_HOURLY';

              const userName = `${userResourceData.resource.firstName} ${userResourceData.resource.lastName}`;

              totalsToSet[userId] = {
                name: userName,
                salaryType,
                baseSalaryType,
                unitCount: isMonthlyBaseSalary
                  ? 1
                  : userTotalAmount.workReducedHours,
                unitCountWithoutReductions: userTotalAmount.workTotalHours,
                unitPrice,
                userId,
                projectTotals: projectTotals,
                assignmentTotals: assignmentTotals,
                eachDayTotalHours: eachDayTotalHours,
              };

              const additionalUserSalaries = Object.values(
                processedData.resources[userId].additionalSalaries
              );

              if (additionalUserSalaries.length > 0) {
                const additionalSalaryRows = additionalUserSalaries.map(
                  (additionalSalary) => {
                    const defaultSalaryTypeData =
                      DEFAULT_ADDITIONAL_SALARY_TYPES[
                        additionalSalary.salaryType
                      ];

                    const {
                      salaryType,
                      unitCount,
                      definedDates,
                      dayUnitCount,
                      baseType,
                      unitPrice,
                    } = additionalSalary;

                    return {
                      salaryName: i18n.t(defaultSalaryTypeData.salaryName),
                      procountorSalaryCode:
                        defaultSalaryTypeData.procountorSalaryCode,
                      salaryType,
                      unitCount: round(unitCount, 2),
                      unitPrice,
                      definedDates,
                      dayUnitCount,
                      unit: 'kpl',
                      userId,
                      userName,
                      baseType,
                    };
                  }
                );

                const existingUserAdditionalSalaries =
                  state.additionalUserSalaryRows[userId];
                if (existingUserAdditionalSalaries) {
                  existingUserAdditionalSalaries.push(...additionalSalaryRows);
                } else {
                  state.additionalUserSalaryRows[userId] = additionalSalaryRows;
                }
              }

              const seperateAdditionalUserSalaries =
                processedData.resources[userId].seperateAdditionalSalaries;

              if (seperateAdditionalUserSalaries.length > 0) {
                const seperateAdditionalSalaryRows =
                  seperateAdditionalUserSalaries.map((additionalSalary) => {
                    const defaultSalaryTypeData =
                      DEFAULT_ADDITIONAL_SALARY_TYPES[
                        additionalSalary.salaryType
                      ];

                    const {
                      salaryType,
                      unitCount,
                      unitPrice,
                      date,
                      ISOStringDate,
                      talosoftaProjectId,
                      // exists when generated from jydacom report
                      jydacomProjectNumber,
                      jydacomAssignmentCode,
                      jydacomAssignmentId,
                      jydacomAssignmentName,
                    } = additionalSalary;

                    return {
                      salaryName: i18n.t(defaultSalaryTypeData.salaryName),
                      salaryType: salaryType,
                      unitCount: round(unitCount, 2),
                      unitPrice,
                      date: date,
                      ISOStringDate,
                      unit: 'kpl',
                      userId,
                      userName,
                      talosoftaProjectId,
                      jydacomProjectNumber,
                      jydacomAssignmentCode,
                      jydacomAssignmentId,
                      jydacomAssignmentName,
                    };
                  });

                const existingSeperateAdditionalUserSalaryRows =
                  state.seperateAdditionalUserSalaryRows[userId];
                if (existingSeperateAdditionalUserSalaryRows) {
                  existingSeperateAdditionalUserSalaryRows.push(
                    seperateAdditionalSalaryRows
                  );
                } else {
                  state.seperateAdditionalUserSalaryRows[userId] =
                    seperateAdditionalSalaryRows;
                }
              }
            }
          );

          state.reportUserRowTotals = totalsToSet;
          state.loading = false;
        }
      )
      .addCase(generateSalaryRowDataFromRecords.fulfilled, (state, action) => {
        const processedData = action.payload.processedData;

        const isRecordBasedReductionsActive =
          state.isRecordBasedReductionsActive;

        state.reportBasicData = {
          fromDate: processedData.fromDate,
          toDate: processedData.toDate,
          reportType: processedData.reportType,
          isRecordBasedReductionsActive,
        };

        let totalsToSet = {};

        const userTotalWorkAmountsByUserId =
          processedData.userTotalWorkAmountsByUserId;

        Object.values(userTotalWorkAmountsByUserId).forEach(
          (userTotalAmount) => {
            const userId = userTotalAmount.userId;
            const userResourceData = processedData.resources[userId];

            const {
              baseSalaryType,
              unitPrice,
              projectTotals,
              eachDayTotalHours,
              personNumber,
              totalWorkDays,
            } = userTotalAmount;

            const isMonthlyBaseSalary = baseSalaryType === 'MONTHLY';

            const salaryType = isMonthlyBaseSalary
              ? 'WORK_SALARY_MONTHLY'
              : 'WORK_SALARY_HOURLY';

            const userName = `${userResourceData.resource.lastName}, ${userResourceData.resource.firstName}`;

            totalsToSet[userId] = {
              name: userName,
              personNumber,
              salaryType,
              baseSalaryType,
              unitCount: isMonthlyBaseSalary
                ? 1
                : userTotalAmount.workReducedHours,
              unitCountWithoutReductions: userTotalAmount.workTotalHours,
              unitPrice,
              userId,
              projectTotals: projectTotals,
              eachDayTotalHours: eachDayTotalHours,
              totalWorkDays,
            };

            const additionalUserSalaries = Object.values(
              processedData.resources[userId].additionalSalaries
            );

            const seperateAdditionalUserSalaries =
              processedData.resources[userId].seperateAdditionalSalaries;

            if (additionalUserSalaries.length > 0) {
              const additionalSalaryRows = additionalUserSalaries.map(
                (additionalSalary) => {
                  const defaultSalaryTypeData =
                    DEFAULT_ADDITIONAL_SALARY_TYPES[
                      additionalSalary.salaryType
                    ];

                  return {
                    salaryName: i18n.t(defaultSalaryTypeData.salaryName),
                    procountorSalaryCode:
                      defaultSalaryTypeData.procountorSalaryCode,
                    salaryType: additionalSalary.salaryType,
                    unitCount: round(additionalSalary.unitCount, 2),
                    definedDates: additionalSalary.definedDates,
                    dayUnitCount: additionalSalary.dayUnitCount,
                    unit: 'kpl',
                    userId,
                    userName,
                  };
                }
              );

              const existingUserAdditionalSalaries =
                state.additionalUserSalaryRows[userId];
              if (existingUserAdditionalSalaries) {
                existingUserAdditionalSalaries.push(additionalSalaryRows);
              } else {
                state.additionalUserSalaryRows[userId] = additionalSalaryRows;
              }
            }

            if (seperateAdditionalUserSalaries.length > 0) {
              const additionalSalaryRows = seperateAdditionalUserSalaries.map(
                (additionalSalary) => {
                  const defaultSalaryTypeData =
                    DEFAULT_ADDITIONAL_SALARY_TYPES[
                      additionalSalary.salaryType
                    ];

                  return {
                    salaryName: i18n.t(defaultSalaryTypeData.salaryName),
                    salaryType: additionalSalary.salaryType,
                    unitCount: round(additionalSalary.unitCount, 2),
                    date: additionalSalary.date,
                    unit: 'kpl',
                    userId,
                    userName,
                    projectId: additionalSalary.projectId,
                  };
                }
              );

              const existingUserAdditionalSalaries =
                state.seperateAdditionalUserSalaryRows[userId];
              if (existingUserAdditionalSalaries) {
                existingUserAdditionalSalaries.push(additionalSalaryRows);
              } else {
                state.seperateAdditionalUserSalaryRows[userId] =
                  additionalSalaryRows;
              }
            }
          }
        );

        state.reportUserRowTotals = totalsToSet;
        state.loading = false;
      })
      .addCase(generateSalaryRows.fulfilled, (state, action) => {
        state.processedSalaryRows = action.payload;
      })
      .addCase(setReportGeneratedFromMode, (state, action) => {
        state.reportGeneratedFromMode = action.payload;
      })
      .addCase(generateProjectEmployeeReport.pending, (state, action) => {
        state.generatedReport = {};
        state.loading = true;
      })
      .addCase(generateProjectEmployeeReport.fulfilled, (state, action) => {
        state.generatedReport = action.payload;
        state.loading = false;
      })
      .addCase(generateProjectListReport.pending, (state, action) => {
        state.generatedReport = {};
        state.loading = true;
      })
      .addCase(generateProjectListReport.fulfilled, (state, action) => {
        state.generatedReport = action.payload;
        state.loading = false;
      })
      .addCase(
        generateProjectOrientatedEmployeesReport.pending,
        (state, action) => {
          state.generatedReport = {};
          state.loading = true;
        }
      )
      .addCase(
        generateProjectOrientatedEmployeesReport.fulfilled,
        (state, action) => {
          state.generatedReport = action.payload;
          state.loading = false;
        }
      )
      .addCase(
        generateProjectOrientatedEmployeesReport.rejected,
        (state, action) => {
          state.loading = false;
        }
      )
      .addCase(sendSalaryData.pending, (state, action) => {
        state.isSendingSalaryData = true;
      })
      .addCase(sendSalaryData.fulfilled, (state, action) => {
        state.isSendingSalaryData = false;
      })
      .addCase(sendSalaryData.rejected, (state, action) => {
        state.isSendingSalaryData = false;
      })
      .addCase(sendJydacomSalaryData.pending, (state, action) => {
        state.isSendingSalaryData = true;
        state.salaryErrorMessage = '';
      })
      .addCase(sendJydacomSalaryData.fulfilled, (state, action) => {
        state.isSendingSalaryData = false;
        state.isConfirmJydacomSalaryDialogOpen = false;
        state.salaryErrorMessage = '';
        enqueueSnackbar('Palkkatiedot lähetetty onnistuneesti', {
          variant: 'success',
        });
      })
      .addCase(sendJydacomSalaryData.rejected, (state, action) => {
        let customError = '';

        const errorPayload = action.payload;

        if (errorPayload) {
          if (errorPayload.errorType === 'missing_worksites') {
            customError = `Työmaita ei löytynyt Jydacomista: ${errorPayload.worksiteCodes?.join(
              ', '
            )}`;
          } else if (errorPayload.errorType === 'missing_phases') {
            customError =
              'Kaikkia litteratietoja ei voitu yhdistää. Lähetys keskeytetty';
          } else if (errorPayload.errorType === 'missing_persons') {
            const personNames = uniq(
              (errorPayload.items || [])
                .map((item) => item._originalSalaryRow?.personName)
                .filter(Boolean)
            );
            const namesList = personNames.length
              ? `: ${personNames.join(', ')}`
              : '';
            customError = `Henkilötietoja ei voitu yhdistää${namesList}. Lähetys keskeytetty`;
          } else if (errorPayload.errorType === 'missing_event_types') {
            customError =
              'Kaikkia palkkatyyppejä ei voitu yhdistää. Lähetys keskeytetty';
          } else if (
            errorPayload.errorType === 'overlapping_sent_salary_dates'
          ) {
            customError =
              'Palkkajakson ajalta löytyi jo tehty lähetys. Lähetys keskeytetty';
          }
        }
        state.salaryErrorMessage = customError;

        state.isSendingSalaryData = false;
        enqueueSnackbar(`Virhe palkkatietojen lähetyksessä. ${customError}`, {
          variant: 'error',
          autoHideDuration: 8000,
        });
      })
      .addCase(openReportSendDialog, (state, action) => {
        state.isReportSendDialogOpen = true;
      })
      .addCase(closeReportSendDialog, (state, action) => {
        state.isReportSendDialogOpen = false;
        state.reportRecipientEmails = [];
      })
      .addCase(addSalaryRowsRecipientEmail, (state, action) => {
        state.reportRecipientEmails.push(action.payload);
      })
      .addCase(removeSalaryRowsRecipientEmail, (state, action) => {
        state.reportRecipientEmails = state.reportRecipientEmails.filter(
          (email) => email !== action.payload
        );
      })
      .addCase(setEmployeeNameToAdd, (state, action) => {
        state.weekIndexedEmployeeNamesToAdd[action.payload.weekIndex] =
          action.payload.name;
      })
      .addCase(setDayWorkHoursOfEmployeeToAdd, (state, action) => {
        const weekIndex = action.payload.weekTableIndex;

        const dayToSet = action.payload.dayToSet;

        const weekIndexedHours = {
          ...state.weekIndexedDayWorkHoursOfEmployeeToAdd,
        };

        const weekDayValues = weekIndexedHours[weekIndex];

        weekDayValues[dayToSet.day] = dayToSet;

        weekIndexedHours[weekIndex] = weekDayValues;

        state.weekIndexedDayWorkHoursOfEmployeeToAdd = weekIndexedHours;
      })
      .addCase(addReportPreviewUserRow.fulfilled, (state, action) => {
        state.tableDataByWeekPeriod = action.payload;
      })
      .addCase(removeReportPreviewUserRow, (state, action) => {
        const userId = action.payload.userId;

        state.tableDataByWeekPeriod.forEach((weeklyTableData, index) => {
          const filteredRows = weeklyTableData.dataRows.filter(
            (weekRow) => weekRow.userId !== userId
          );
          state.tableDataByWeekPeriod[index] = {
            ...state.tableDataByWeekPeriod[index],
            dataRows: filteredRows,
          };
        });

        delete state.reportUserRowTotals[userId];
        delete state.additionalUserSalaryRows[userId];
        delete state.seperateAdditionalUserSalaryRows[userId];
      })
      .addCase(addManualUserRowTotals, (state, action) => {
        const existingManualUserRow = Object.values(
          state.reportUserRowTotals
        ).find((userRow) => userRow.name === action.payload.name);

        if (existingManualUserRow) {
          const combinedUnitCountWithoutReductions =
            (existingManualUserRow.unitCountWithoutReductions +=
              action.payload.unitCountWithoutReductions);

          const combinedUnitCount = (existingManualUserRow.unitCount +=
            action.payload.unitCount);

          state.reportUserRowTotals[action.payload.userId] = {
            ...action.payload,
            unitCountWithoutReductions: combinedUnitCountWithoutReductions,
            unitCount: combinedUnitCount,
          };
        } else {
          state.reportUserRowTotals[action.payload.userId] = action.payload;
        }
      })
      .addCase(initAddEmployeeForm, (state, action) => {
        const weekTableIndex = action.payload;

        const weekPeriodDays =
          state.tableDataByWeekPeriod[weekTableIndex].headerRow;

        const defaultValues = weekPeriodDays.map((dayValue) => {
          const { date } = returnDateAndIsoWeekDayFromReportDayValue(dayValue);
          return {
            day: date,
            value: '',
          };
        });

        const weekIndexedHours = state.weekIndexedDayWorkHoursOfEmployeeToAdd;

        weekIndexedHours[weekTableIndex] = arrayToMap(defaultValues, 'day');

        state.weekIndexedDayWorkHoursOfEmployeeToAdd = weekIndexedHours;
      })
      .addCase(openRowCommentDialog, (state, action) => {
        state.isRowCommentDialogOpen = true;
        state.selectedRowCommentData = action.payload;
      })
      .addCase(closeRowCommentDialog, (state, action) => {
        state.isRowCommentDialogOpen = false;
        state.selectedRowCommentData = {};
      })
      .addCase(setRowComment, (state, action) => {
        const comment = action.payload.comment;
        const rowCommentId = action.payload.salaryType
          ? `${action.payload.userId}-${action.payload.salaryType}`
          : `${action.payload.userId}`;

        state.rowComments[rowCommentId] = comment;
        state.selectedRowCommentData = {};
        state.isRowCommentDialogOpen = false;
      })
      .addCase(closeReportPreviewDialog, (state, action) => {
        state.rowComments = {};
        state.additionalUserSalaryRows = {};
        state.seperateAdditionalUserSalaryRows = {};
        state.weekIndexedEmployeeNamesToAdd = {};
        state.currentOpenHistoryReportIndex = 0;
        state.isReportsHistoryOpen = false;
      })
      .addCase(openAdditionalUserSalaryRowDialog, (state, action) => {
        state.isAdditionalSalaryRowDialogOpen = true;
        state.additionalUserSalaryRowDialogData = action.payload;
      })
      .addCase(closeAdditionalUserSalaryRowDialog, (state, action) => {
        state.isAdditionalSalaryRowDialogOpen = false;
      })
      .addCase(removeReportPreviewUserAdditionalSalaryRow, (state, action) => {
        const userId = action.payload.userId;

        state.additionalUserSalaryRows[userId] = state.additionalUserSalaryRows[
          userId
        ].filter((row) => row.salaryType !== action.payload.salaryType);

        state.seperateAdditionalUserSalaryRows[userId] =
          state.seperateAdditionalUserSalaryRows[userId].filter(
            (row) => row.salaryType !== action.payload.salaryType
          );
      })
      .addCase(addAdditionalUserSalaryRow, (state, action) => {
        if (!state.additionalUserSalaryRows[action.payload.userId]) {
          state.additionalUserSalaryRows[action.payload.userId] = [
            action.payload,
          ];
        } else {
          state.additionalUserSalaryRows[action.payload.userId].push(
            action.payload
          );
        }
        state.isAdditionalSalaryRowDialogOpen = false;
      })
      .addCase(loadProjectSavedReports.fulfilled, (state, action) => {
        savedReportsAdapter.setMany(state.savedReports, action.payload);
      })
      .addCase(deleteReportDraft.pending, (state, action) => {
        state.isDeletingReportDraft = true;
      })
      .addCase(deleteReportDraft.fulfilled, (state, action) => {
        savedReportsAdapter.upsertOne(state.savedReports, action.payload);
        enqueueSnackbar(i18n.t('report.draft_deletion_success'), {
          variant: 'success',
        });
        state.isDeletingReportDraft = false;
        state.isConfirmReportDraftDeleteDialogOpen = false;
        state.selectedReportDraftId = '';
      })
      .addCase(deleteReportDraft.rejected, (state, action) => {
        state.isDeletingReportDraft = false;
        enqueueSnackbar(i18n.t('report.draft_deletion_failed'), {
          variant: 'error',
        });
      })
      .addCase(openReportsHistory, (state, action) => {
        state.isReportsHistoryOpen = !state.isReportsHistoryOpen;
      })
      .addCase(openPreviousReport, (state, action) => {
        state.currentOpenHistoryReportIndex += 1;
      })
      .addCase(openNextReport, (state, action) => {
        state.currentOpenHistoryReportIndex -= 1;
      })
      .addCase(openReportDraftsDialog, (state, action) => {
        state.isReportDraftsDialogOpen = true;
      })
      .addCase(closeReportDraftsDialog, (state, action) => {
        state.isReportDraftsDialogOpen = false;
        state.additionalUserSalaryRows = {};
        state.seperateAdditionalUserSalaryRows = {};
        state.rowComments = {};
        state.weekIndexedEmployeeNamesToAdd = {};
        state.selectedReportDraftId = '';
        state.currentOpenHistoryReportIndex = 0;
      })
      .addCase(setSelectedReportDraftId, (state, action) => {
        state.selectedReportDraftId = action.payload;
      })
      .addCase(setInitialReportEditValuesOfDraft, (state, action) => {
        const reportDraftData = action.payload;

        const {
          fromDate,
          toDate,
          reportType,
          tableDataByWeekPeriod,
          totals,
          userAdditionalSalaryRows,
          rowComments,
        } = reportDraftData;

        state.reportBasicData = {
          fromDate,
          toDate,
          reportType,
        };

        state.reportUserRowTotals = arrayToMap(Object.values(totals), 'userId');

        state.rowComments = rowComments;

        state.additionalUserSalaryRows = userAdditionalSalaryRows;

        state.tableDataByWeekPeriod = tableDataByWeekPeriod;
      })
      .addCase(toggleRecordBasedReductions, (state, action) => {
        state.isRecordBasedReductionsActive =
          !state.isRecordBasedReductionsActive;
      })

      .addCase(
        toggleIsAutomaticMealBreakReductionsAppliedToRecordBasedReductions,
        (state, action) => {
          state.isAutomaticMealBreakReductionsAppliedToRecordBasedReductions =
            !state.isAutomaticMealBreakReductionsAppliedToRecordBasedReductions;
        }
      )
      .addCase(openConfirmReportDraftDeleteDialog, (state, action) => {
        state.isConfirmReportDraftDeleteDialogOpen = true;
      })
      .addCase(closeConfirmReportDraftDeleteDialog, (state, action) => {
        state.isConfirmReportDraftDeleteDialogOpen = false;
      })
      .addCase(toggleIsReportProjectRowsEnabled, (state, action) => {
        state.isReportProjectHourRowsEnabled =
          !state.isReportProjectHourRowsEnabled;
      })
      .addCase(toggleIncludeOnlyApprovedRecords, (state, action) => {
        state.isIncludeOnlyApprovedRecordsEnabled =
          !state.isIncludeOnlyApprovedRecordsEnabled;
      })
      .addCase(generateBalancoSalaryReport.fulfilled, (state, action) => {
        state.additionalUserSalaryRows = {};
        state.seperateAdditionalUserSalaryRows = {};
        state.reportBasicData = {};
        state.reportUserRowTotals = {};
      })
      .addCase(resetLoadingReportState, (state, action) => {
        state.loading = false;
      })
      .addCase(setIsConfirmJydacomSalaryDialogOpen, (state, action) => {
        state.isConfirmJydacomSalaryDialogOpen = action.payload;
        state.salaryErrorMessage = '';
      })
      .addCase(setPaidCoffeeBreakMinutes, (state, action) => {
        state.paidCoffeeBreakMinutes = action.payload;
      })
      .addCase(resetPreReportDialogSettings, (state, action) => {
        state.isRecordBasedReductionsActive = false;
        state.isReportProjectHourRowsEnabled = false;
        state.paidCoffeeBreakMinutes = 15;
      });
  },
});
